import { useAuthStore } from './useAuthStore';
import { captureChannelContext, captureChannelRevision, useChannelStore } from './useChannelStore';
import { useChannelMemberStore } from './useChannelMemberStore';
import { useMessageStore } from './useMessageStore';
import { useDmStore } from './useDmStore';
import { usePresenceStore } from './usePresenceStore';
import { useTypingStore } from './useTypingStore';
import { useThreadStore } from './useThreadStore';
import { useAgentActivityStore } from './useAgentActivityStore';
import { useNormalizedActivityStore } from './useNormalizedActivityStore';
import { useDshSessionStore } from './useDshSessionStore';
import { resolveNormalizedSessionOwner } from '../lib/normalized-session-scope';
import { useNormalizedConversationStore } from './useNormalizedConversationStore';
import { useServerStore } from './useServerStore';
import { useTaskStore } from './useTaskStore';
import { useWsStore } from './useWsStore';
import * as api from '../lib/api';
import { randomUUID } from '../lib/uuid';
import { runWithConcurrency } from '../lib/concurrency';
import type { Message, PendingMessage } from '../types';

// How long a sent message may wait for its ack before the timeline calls it
// failed and offers a retry.
const ACK_TIMEOUT_MS = 10_000;

// How many channel windows a reconnect re-reads at once. A member of many
// channels would otherwise put one request per channel on the wire in the same
// tick, at a server that has just become reachable again.
const RESYNC_CONCURRENCY = 4;

export interface OutgoingMessage {
  channelId: string;
  content: string;
  contentType: PendingMessage['contentType'];
  mentions?: string[];
}

/**
 * Puts a message on the wire and into the timeline as a single act, and returns
 * the client id its ack reconciles against.
 *
 * The optimistic row is what carries the message, because the socket writes a
 * frame only while it is open and says nothing when it is not: without the row
 * a message sent across a reconnect would leave no trace to notice or retry
 * from. Every surface that sends — a channel, a DM, a reply, the first reply
 * that brings a thread into existence — goes through here, so they all fail the
 * same visible way.
 */
export function dispatchOutgoingMessage(outgoing: OutgoingMessage): string {
  const currentUser = useAuthStore.getState().currentUser;
  const clientMessageId = randomUUID();

  useMessageStore.getState().addPendingMessage({
    clientMessageId,
    channelId: outgoing.channelId,
    content: outgoing.content,
    contentType: outgoing.contentType,
    status: 'pending',
    createdAt: Date.now(),
    senderName: currentUser?.display_name ?? 'Unknown',
    senderId: currentUser?.id ?? '',
    mentions: outgoing.mentions,
  });

  useWsStore.getState().sendWsMessage({
    type: 'send_message',
    channel_id: outgoing.channelId,
    content: outgoing.content,
    content_type: outgoing.contentType,
    client_message_id: clientMessageId,
  });

  const timer = setTimeout(() => {
    useMessageStore.getState().failPendingMessage(clientMessageId, outgoing.channelId);
  }, ACK_TIMEOUT_MS);
  useWsStore.getState().registerAckTimer(clientMessageId, () => clearTimeout(timer));

  return clientMessageId;
}

export function onMessageReceived(channelId: string, message: Message) {
  const currentUserId = useAuthStore.getState().currentUser?.id ?? null;
  const currentChannelId = useChannelStore.getState().currentChannelId;

  useMessageStore.getState().addMessage(channelId, message, currentUserId, currentChannelId);

  // The message is what the sender was announcing, so the announcement is over.
  // Waiting out the keepalive's expiry instead would leave an agent that types,
  // posts, types and posts again flickering on the bar it shares with everyone
  // else who is active here.
  useTypingStore.getState().clearTyping(channelId, message.sender_id);

  useChannelStore.getState().updateChannel(channelId, { last_message_at: message.created_at });
  if (currentChannelId !== channelId && message.sender_id !== currentUserId) {
    const channels = useChannelStore.getState().channels;
    const channel = channels.find(c => c.id === channelId);
    const currentUnread = channel?.unread_count ?? 0;
    useChannelStore.getState().updateUnread(channelId, currentUnread + 1);
  }

  const dmChannels = useDmStore.getState().dmChannels;
  if (dmChannels.some(dm => dm.id === channelId)) {
    useDmStore.getState().updateDmChannel(channelId, {
      last_message: { content: message.content, created_at: message.created_at },
    });
    if (currentChannelId !== channelId && message.sender_id !== currentUserId) {
      const dm = dmChannels.find(d => d.id === channelId);
      if (dm) {
        useDmStore.getState().updateDmChannel(channelId, {
          unread_count: (dm.unread_count ?? 0) + 1,
        });
      }
    }
  }
}

export function onMessageAcked(channelId: string, clientMessageId: string, serverMessage: Message) {
  useMessageStore.getState().ackPendingMessage(clientMessageId, channelId, serverMessage);
  useChannelStore.getState().updateChannel(channelId, { last_message_at: serverMessage.created_at });

  const dmChannels = useDmStore.getState().dmChannels;
  if (dmChannels.some(dm => dm.id === channelId)) {
    useDmStore.getState().updateDmChannel(channelId, {
      last_message: { content: serverMessage.content, created_at: serverMessage.created_at },
    });
  }
}

export function onChannelLeave(channelId: string) {
  useNormalizedConversationStore.getState().clearChannel(channelId);
  const owner = resolveNormalizedSessionOwner(channelId);
  if (owner) useDshSessionStore.getState().unloadChannel(owner.accountId, owner.guildId, channelId);
  useChannelStore.getState().navigateAfterLeave(channelId);
  useChannelMemberStore.getState().clearChannel(channelId);
  useMessageStore.getState().navigateAfterLeave(channelId);
  useTypingStore.getState().clearChannel(channelId);
  useThreadStore.getState().clearChannel(channelId);
  useAgentActivityStore.getState().clearChannel(channelId);
  useNormalizedActivityStore.getState().clearChannel(channelId);
}

export interface ReconnectResync {
  /** Subscriptions that no longer name anything the member can read. */
  staleSubscriptions: string[];
  /** Per channel, the messages the re-read found that the client did not hold. */
  learnedMessages: Record<string, Message[]>;
}

/** Per channel, the ids of the messages it held at a moment its range was still whole. */
export type HeldMessages = ReadonlyMap<string, ReadonlySet<string>>;

/**
 * Freezes what every channel holds while each range is still known unbroken.
 *
 * Only a row from one of these ranges can prove a re-read's window reaches what
 * the client already had. The socket resubscribes the moment it reopens and
 * starts delivering again while the repair is still reading, so a row that
 * arrived since may itself sit above the stretch the gap swallowed, and would
 * vouch for a join across it. Taking this before the repair's first await keeps
 * it clear of those rows — a frame is dispatched in a later turn than the one
 * that opens the socket — and the same snapshot serves every retry, since an
 * attempt that fails still lets frames in while it runs.
 */
export function heldMessagesAtGapEnd(): HeldMessages {
  return new Map(
    Object.entries(useMessageStore.getState().messages).map(
      ([channelId, messages]) => [channelId, new Set(messages.map(m => m.id))] as const,
    ),
  );
}

/**
 * Repairs what only the channel the member is looking at renders: its reply
 * badges, its member roster, and its own unread badge. Every other channel
 * rebuilds all three when it is entered, so one channel's worth of reads covers
 * the drift a member can actually see.
 *
 * The unread badge needs the read mark moved, not just cleared locally: the
 * member has been in this channel the whole time, so a count the server computes
 * from a mark that last moved when they entered it badges messages they have
 * already watched arrive, and re-badges them on the next listing read. Being in
 * the channel is what reading it means here — the live path bumps no unread for
 * the channel on screen either.
 */
async function repairOpenChannel(channelId: string): Promise<void> {
  const reads = [useThreadStore.getState().loadThreads(channelId)];
  // The listing has just been re-read, so it says whether the member is still
  // in this channel — and the server takes a read mark from members only.
  const listed = useChannelStore.getState().channels.find(c => c.id === channelId);
  const isDm = useDmStore.getState().dmChannels.some(dm => dm.id === channelId);
  if (listed?.is_member === true || isDm) {
    useChannelStore.getState().bumpChannelMembersVersion(channelId);
    useChannelStore.getState().clearUnread(channelId);
    useDmStore.getState().clearUnread(channelId);
    reads.push(api.markChannelRead(channelId));
  }
  await Promise.all(reads);
}

/**
 * Rebuilds, from the primary tables, every view the socket would have kept
 * current across a gap in which its pushes were missed.
 *
 * Nothing is patched: the listings are re-read whole, so a channel that
 * disappeared while the socket was down disappears here too, and unread counts
 * come back server-computed rather than accumulated from frames that were never
 * delivered. Only channels the client already holds a window for are re-read —
 * a channel it never opened has no local state to have drifted, and will be
 * read fresh when it is entered.
 *
 * Stale subscriptions are reported rather than dropped here because the socket
 * belongs to the caller, which must also tell the server about them, and the
 * messages each re-read newly learned are reported for the same reason: only
 * the caller holds the pending sends they resolve.
 */
export async function resyncAfterReconnect(
  subscribedChannelIds: ReadonlySet<string>,
  heldAtGapEnd: HeldMessages,
  options: {
    refreshChannels?: boolean;
    listedBefore?: ReadonlySet<string>;
    isCurrent?: () => boolean;
    onMessagesLearned?: (channelId: string, messages: Message[]) => void;
    activeServerChannelId?: () => string | null;
  } = {},
): Promise<ReconnectResync> {
  const sameContext = options.isCurrent ?? captureChannelContext();
  let sameRevision = () => true;
  const isCurrent = () => sameContext() && sameRevision();
  const cancelled = { staleSubscriptions: [], learnedMessages: {} };
  if (!isCurrent()) return cancelled;
  const listedBefore = options.listedBefore ?? new Set([
    ...useChannelStore.getState().channels.map(c => c.id),
    ...useDmStore.getState().dmChannels.map(dm => dm.id),
  ]);

  const [, dmChannels] = await Promise.all([
    options.refreshChannels === false ? Promise.resolve() : useChannelStore.getState().invalidateChannels().then(() => {
      sameRevision = captureChannelRevision();
    }),
    api.fetchDmChannels(),
  ]);
  if (!isCurrent()) return cancelled;
  useDmStore.getState().setDmChannels(dmChannels);

  const activeServerChannelId = options.activeServerChannelId?.();
  const readable = new Set([
    ...useChannelStore.getState().channels.filter((channel) => channel.is_member === true ||
      (channel.channel_type === 0 && channel.visibility === 'public' &&
        channel.id === activeServerChannelId && subscribedChannelIds.has(channel.id))).map((channel) => channel.id),
    ...useDmStore.getState().dmChannels.map(dm => dm.id),
  ]);
  // A thread is subscribed by id and appears in no listing, so only an id a
  // listing used to carry can be judged gone by its absence from one.
  const staleSubscriptions = [...subscribedChannelIds].filter(
    id => listedBefore.has(id) && !readable.has(id),
  );

  const stale = new Set(staleSubscriptions);
  const cached = useMessageStore.getState().messages;
  const targets = [...subscribedChannelIds].filter(id => !stale.has(id) && cached[id] !== undefined);

  const currentChannelId = useChannelStore.getState().currentChannelId;
  const openChannelRepair = currentChannelId
    ? repairOpenChannel(currentChannelId)
    : Promise.resolve();

  const learnedMessages: Record<string, Message[]> = {};
  await Promise.all([
    openChannelRepair,
    // One channel's read failing must not cost the others theirs, nor the
    // caller its subscription reconciliation.
    runWithConcurrency(targets, RESYNC_CONCURRENCY, async (channelId) => {
      if (!isCurrent()) return;
      try {
        learnedMessages[channelId] = await useMessageStore.getState().resyncMessages(
          channelId, heldAtGapEnd.get(channelId) ?? new Set(),
        );
        options.onMessagesLearned?.(channelId, learnedMessages[channelId]);
      } catch (err) {
        console.warn('[reconnect] message resync failed for %s:', channelId, err);
      }
    }),
  ]);
  if (!isCurrent()) return cancelled;
  return { staleSubscriptions, learnedMessages };
}

export function resetAllStores() {
  useAuthStore.getState().reset();
  useChannelStore.getState().reset();
  useChannelMemberStore.getState().reset();
  useMessageStore.getState().reset();
  useDmStore.getState().reset();
  usePresenceStore.getState().reset();
  useTypingStore.getState().reset();
  useThreadStore.getState().reset();
  useServerStore.getState().reset();
  useTaskStore.getState().reset();
  useAgentActivityStore.getState().reset();
  useNormalizedActivityStore.getState().reset();
  useDshSessionStore.getState().resetMemory();
  useNormalizedConversationStore.getState().reset();
}

/** Clears every cache whose contents are authorized by the active Server. */
export function resetServerScopedStores() {
  useChannelStore.getState().reset();
  useChannelMemberStore.getState().reset();
  useMessageStore.getState().reset();
  useTypingStore.getState().reset();
  useThreadStore.getState().reset();
  useServerStore.getState().clearMembers();
  useTaskStore.getState().reset();
  useAgentActivityStore.getState().reset();
  useNormalizedActivityStore.getState().reset();
  useDshSessionStore.getState().resetMemory(useAuthStore.getState().currentUser?.id);
  useNormalizedConversationStore.getState().resetServer(useAuthStore.getState().currentUser?.id);
}
